import React, { useEffect } from 'react';
import { Box, Button, CircularProgress, Grow, Stack, Typography, SxProps, Theme } from '@mui/material';
import { tokens } from '../../theme';

interface FormSectionProps {
  title: React.ReactNode;
  description?: React.ReactNode;
  children: React.ReactNode;
  /** Bez linii nad sekcja (pierwsza sekcja w karcie). */
  first?: boolean;
  sx?: SxProps<Theme>;
}

/**
 * Sekcja formularza: po lewej tytul i opis, po prawej pola.
 * Na telefonie opis jest nad polami.
 */
export const FormSection: React.FC<FormSectionProps> = ({ title, description, children, first, sx }) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: '240px minmax(0, 1fr)' },
      gap: { xs: 1.5, md: 4 },
      py: { xs: 2.5, md: 3 },
      borderTop: first ? 'none' : `1px solid ${tokens.line}`,
      ...(first ? { pt: { xs: 0, md: 0 } } : {}),
      ...((sx as object) || {}),
    }}
  >
    <Box>
      <Typography component="h3" sx={{ fontSize: '1rem', fontWeight: 700, lineHeight: 1.4 }}>{title}</Typography>
      {description && (
        <Typography component="div" sx={{ mt: 0.5, fontSize: '0.875rem', color: tokens.muted, lineHeight: 1.5 }}>{description}</Typography>
      )}
    </Box>
    <Box sx={{ minWidth: 0 }}>{children}</Box>
  </Box>
);

/** Siatka pol formularza: 1 kolumna na telefonie, `columns` od tabletu. */
export const FieldGrid: React.FC<{ columns?: 1 | 2 | 3; children: React.ReactNode; sx?: SxProps<Theme> }> = ({ columns = 2, children, sx }) => (
  <Box
    sx={{
      display: 'grid',
      gap: 2,
      gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: `repeat(${Math.min(columns, 2)}, minmax(0, 1fr))`, lg: `repeat(${columns}, minmax(0, 1fr))` },
      alignItems: 'start',
      ...((sx as object) || {}),
    }}
  >
    {children}
  </Box>
);

interface SaveBarProps {
  /** Widoczny, gdy sa niezapisane zmiany. */
  open: boolean;
  onSave: () => void;
  onDiscard?: () => void;
  saving?: boolean;
  message?: React.ReactNode;
  saveLabel?: string;
  discardLabel?: string;
  disabled?: boolean;
}

/** Jeden pasek zapisu dla calego ekranu, przyklejony do dolu tresci. */
export const SaveBar: React.FC<SaveBarProps> = ({
  open, onSave, onDiscard, saving, message = 'Masz niezapisane zmiany', saveLabel = 'Zapisz zmiany', discardLabel = 'Odrzuć', disabled,
}) => (
  <Box sx={{ position: 'sticky', bottom: { xs: 12, md: 20 }, zIndex: 30, pointerEvents: 'none', mt: 2 }}>
    <Grow in={open} unmountOnExit>
      <Stack
        direction="row"
        alignItems="center"
        gap={1.25}
        flexWrap="wrap"
        role="region"
        aria-label="Niezapisane zmiany"
        sx={{
          pointerEvents: 'auto',
          maxWidth: 720,
          mx: 'auto',
          bgcolor: tokens.ink,
          color: '#EEEAF5',
          borderRadius: '14px',
          pl: 2.25,
          pr: 1.25,
          py: 1.25,
          boxShadow: '0 14px 36px rgba(28,23,38,0.28)',
        }}
      >
        <Typography component="div" sx={{ flex: 1, minWidth: 160, fontSize: '0.90625rem', color: '#EEEAF5' }}>{message}</Typography>
        {onDiscard && (
          <Button
            size="small"
            variant="outlined"
            onClick={onDiscard}
            disabled={saving}
            sx={{ bgcolor: 'transparent', color: '#EEEAF5', borderColor: 'rgba(255,255,255,0.24)', '&:hover': { bgcolor: 'rgba(255,255,255,0.06)', borderColor: 'rgba(255,255,255,0.4)' } }}
          >
            {discardLabel}
          </Button>
        )}
        <Button
          size="small"
          variant="contained"
          onClick={onSave}
          disabled={saving || disabled}
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : undefined}
          sx={{ '&.Mui-disabled': { bgcolor: 'rgba(255,255,255,0.14)', color: 'rgba(255,255,255,0.6)' } }}
        >
          {saving ? 'Zapisywanie…' : saveLabel}
        </Button>
      </Stack>
    </Grow>
  </Box>
);

/** Ostrzezenie przegladarki przy zamykaniu karty z niezapisanymi zmianami. */
export const useUnsavedChangesWarning = (dirty: boolean) => {
  useEffect(() => {
    if (!dirty) return undefined;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);
};

export default FormSection;
